/* eslint-disable */
import Framework, { shapes } from '@greenville/framework';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import Chip from '@mui/material/Chip';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import {
  ReactFlow,
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  Controls,
  MarkerType,
  // MiniMap,
  ReactFlowProvider,
  useKeyPress,
  useReactFlow,
  ControlButton
} from '@xyflow/react';
// default styling
import '@xyflow/react/dist/style.css';
// or if you just want basic styles
// import '@xyflow/react/dist/base.css';

// import { ThemeProvider, createTheme, withStyles } from '@material-ui/core/styles';
import './index.css';

// Import components and styles from separate files
import axios from 'axios';
import _ from 'lodash';
import { toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as constants from '../../common/constants';
import env from '../../common/env';
import utils from '../../common/utils';
import CustomConnectionLine from './components/CustomConnectionLine';
import { CustomEdge, EditEdgeModal } from './components/EdgeComponents';
import EditNodeModal from './components/EditNodeModal';
import { checkEdgeExists, isConnectionValid } from './components/FlowUtils';
import GraphTester from './components/GraphTester';
import Header from './components/header/Header';
import ModalPopup from './components/ModalPopup';
import { AgentNode, CustomGroup, EndNode, StartNode, SupervisorNode, ToolNode } from './components/NodeComponents';
import { buttonStyles, contextMenuStyles, edgeStyles, sidebarStyles } from './components/NodeStyles';
import SidePanel from './components/sidePanel/SidePanel';
import { groupColors, groupColorsLight, INFO_BTN, TEST_BTN, THEME_BTN } from './constants/StyleConstants';
import useNodeActions from './hooks/useNodeActions';
import { CloseFullscreen, OpenInFull } from '@mui/icons-material';
import { GRAPH_ADMIN, DEFAULT_TENANT_NAME } from './constants/AgenticTextConstants';
import * as serviceConstants from './constants/ServiceConstants';
import TokenService from './service/TokenService';
import TenantsModel from './models/TenantsModel';
import Instructions from './components/Instructions';
import { transformEdge, transformNode } from './utils';
import Confirmation from '../../common/components/ConfirmationDialog/Confirmation';

// Define edge types dictionary
const edgeTypes = {
  custom: CustomEdge
};

const initialNodes = [];
const initialEdges = [];
let globalGraphs;

// Create a separate component for the flow content outside of GraphEditor
const Flow = React.memo(
  ({
    nodes,
    edges,
    setNodes,
    setEdges,
    isConditionalEdge,
    onNodeDoubleClick,
    theme,
    containerRef,
    isFullScreen,
    setIsFullScreen
  }) => {
    const deleteKeyPressed = useKeyPress('Delete');
    const [contextMenu, setContextMenu] = useState(null);
    const [selectedEdge, setSelectedEdge] = useState(null);
    const [isEdgeModalOpen, setIsEdgeModalOpen] = useState(false);
    const processedEdgeIdsRef = useRef(new Set());
    const edgesRef = useRef(edges);
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [selectedEdgeId, setSelectedEdgeId] = useState(null);
    const { handleNodeDragStop, handleUngroupNode, handleNodeDetach } = useNodeActions();
    const { fitView } = useReactFlow();

    const toggleFullscreen = async () => {
      const element = containerRef.current;

      const isFs =
        document.fullscreenElement === element ||
        document.webkitFullscreenElement === element ||
        document.msFullscreenElement === element;

      if (!isFs) {
        await element.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    };

    useEffect(() => {
      const handleChange = () => {
        const isNowFs =
          document.fullscreenElement === containerRef.current ||
          document.webkitFullscreenElement === containerRef.current ||
          document.msFullscreenElement === containerRef.current;
        setIsFullScreen(isNowFs);

        setTimeout(() => {
          fitView({ duration: 300 });
        }, 100);
      };

      document.addEventListener('fullscreenchange', handleChange);
      return () => {
        document.removeEventListener('fullscreenchange', handleChange);
      };
    }, [containerRef, fitView]);

    const getNodeTypes = (theme) => ({
      start: (props) => <StartNode {...props} theme={theme} />,
      end: (props) => <EndNode {...props} theme={theme} />,
      agent: (props) => <AgentNode {...props} theme={theme} />,
      tool: (props) => <ToolNode {...props} theme={theme} />,
      supervisor: (props) => <SupervisorNode {...props} theme={theme} />,
      group: CustomGroup
    });

    const nodeTypes = useMemo(() => getNodeTypes(theme), [theme]);

    // Update edges ref when edges change
    useEffect(() => {
      edgesRef.current = edges;
    }, [edges]);

    // Function to delete selected elements
    const deleteSelectedElements = useCallback(() => {
      setNodes((nds) => {
        const selectedNodeIds = nds.filter((node) => node.selected).map((n) => n.id);
        const remainingNodes = nds.filter((node) => !node.selected);
        setEdges((eds) =>
          eds.filter(
            (edge) => !edge.selected && !selectedNodeIds.includes(edge.source) && !selectedNodeIds.includes(edge.target)
          )
        );
        return remainingNodes;
      });
    }, [setNodes, setEdges]);

    // Delete selected elements when Delete key is pressed
    useEffect(() => {
      if (deleteKeyPressed) {
        deleteSelectedElements();
      }
    }, [deleteKeyPressed, deleteSelectedElements]);

    // Handle node changes
    const onNodesChange = useCallback(
      (changes) => {
        setNodes((nds) => applyNodeChanges(changes, nds));
      },
      [setNodes]
    );

    // Handle edge changes
    const onEdgesChange = useCallback(
      (changes) => {
        setEdges((eds) => {
          const updatedEdges = applyEdgeChanges(changes, eds);
          return updatedEdges.map((edge) => {
            const sourceNode = nodes.find((n) => n.id === edge.source);
            const targetNode = nodes.find((n) => n.id === edge.target);
            const sourceType = sourceNode?.type || 'agent';
            const targetType = targetNode?.type || 'agent';

            let edgeColor;
            if (sourceType === 'agent' && targetType === 'tool') {
              edgeColor = '#a78bfa';
            } else if (sourceType === 'tool' && targetType === 'agent') {
              edgeColor = '#fcd34d';
            } else if (sourceType === 'start') {
              edgeColor = '#4ade80';
            } else if (sourceType === 'end') {
              edgeColor = '#fb923c';
            } else if (sourceType === 'supervisor') {
              edgeColor = '#eb6134';
            } else if (sourceType === 'agent' && targetType === 'supervisor') {
              edgeColor = '#eb6134';
            } else if (sourceType === 'supervisor' && targetType === 'supervisor') {
              edgeColor = '#eb6134';
            } else {
              edgeColor = '#a78bfa';
            }

            return {
              ...edge,
              type: 'custom',
              animated: true,
              style: {
                stroke: edgeColor,
                strokeWidth: 2,
                strokeDasharray: edge.data?.conditional ? '5,5' : 'none'
              },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: edgeColor
              }
            };
          });
        });
      },
      [nodes, setEdges]
    );

    // Handle edge update with condition
    const handleEdgeUpdate = useCallback(
      (updatedEdge) => {
        setEdges((edges) => {
          // If this is a new edge being created from the modal
          if (!edges.find((e) => e.id === selectedEdge.id)) {
            // Add the new edge with the condition
            return [
              ...edges,
              {
                ...selectedEdge,
                data: {
                  ...selectedEdge.data,
                  ...updatedEdge,
                  conditional: true,
                  label: 'condition'
                },
                style: {
                  ...selectedEdge.style,
                  strokeDasharray: '5,5'
                }
              }
            ];
          }
          // Update existing edge
          return edges.map((edge) => (edge.id === selectedEdge.id ? { ...edge, data: updatedEdge } : edge));
        });
        setIsEdgeModalOpen(false);
        setSelectedEdge(null);
      },
      [selectedEdge, setEdges]
    );

    // Handle new connections
    const onConnect = useCallback(
      (params) => {
        if (!params.source || !params.target) return;

        const sourceNode = nodes.find((n) => n.id === params.source);
        const targetNode = nodes.find((n) => n.id === params.target);
        if (checkEdgeExists(sourceNode?.id, targetNode?.id, edgesRef?.current)) {
          console.log(`Edge already exists: ${sourceNode.id} → ${targetNode.id}`);
          return;
        }
        if (!sourceNode || !targetNode) return;
        if (params.source === params.target) {
          console.log('Cannot connect a node to itself');
          return;
        }

        const sourceType = sourceNode.type;
        const targetType = targetNode.type;

        if (!isConnectionValid(params.source, params.target, nodes)) {
          console.log(`Invalid connection: ${sourceType} → ${targetType}`);
          return;
        }

        // Create a new edge with proper source and target
        let newEdgeParams = { ...params };

        // For agent-to-tool connections, ensure agent is source and tool is target
        if (sourceType === 'agent' && targetType === 'tool') {
          // This is already correct, no need to swap
          console.log('Creating agent-to-tool connection:', params.source, '→', params.target);
        } else if (sourceType === 'tool' && targetType === 'agent') {
          // Swap source and target for tool-to-agent connections
          newEdgeParams = {
            ...params,
            source: params.target,
            target: params.source
          };
          console.log(
            'Swapping tool-to-agent connection:',
            params.source,
            '→',
            params.target,
            'to',
            newEdgeParams.source,
            '→',
            newEdgeParams.target
          );
        }

        const newEdge = {
          ...newEdgeParams,
          id: `edge-${params.source}-${params.target}-${Date.now()}`,
          type: 'custom',
          animated: true,
          data: {
            sourceType,
            targetType,
            conditional: isConditionalEdge,
            label: isConditionalEdge ? 'condition' : 'continue',
            conditionExpression: isConditionalEdge ? 'state.messages.at(-1).content.includes("summarize")' : undefined
          },
          style: {
            stroke: getStrokeColor(sourceType),
            strokeDasharray: isConditionalEdge ? '5,5' : 'none'
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: getStrokeColor(sourceType)
          }
        };

        // If this is a conditional edge between agents, show the condition configuration modal
        if (isConditionalEdge && sourceType === 'agent' && targetType === 'agent') {
          console.log('Creating conditional edge between agents:', sourceNode.id, '→', targetNode.id);
          setSelectedEdge(newEdge);
          setIsEdgeModalOpen(true);
        } else {
          setEdges((eds) => [...eds, newEdge]);
        }
      },
      [nodes, isConditionalEdge, setEdges]
    );

    const getStrokeColor = (sourceType) => {
      if (sourceType === 'agent') {
        return '#a78bfa';
      } else if (sourceType === 'supervisor') {
        return '#eb6134';
      } else if (sourceType === 'start') {
        return '#4ade80';
      } else {
        return '#fcd34d';
      }
    };

    // Handle edge click
    const handleEdgeClick = useCallback((event, edge) => {
      event.stopPropagation();
      setSelectedNodeId(null);

      setSelectedEdgeId((prevId) => {
        const isSameEdge = prevId === edge.id;
        if (isSameEdge) {
          setSelectedEdge(null);
          setIsEdgeModalOpen(false);
          return null;
        } else {
          setSelectedEdge(edge);
          setIsEdgeModalOpen(true);
          return edge.id;
        }
      });
    }, []);

    // Handle context menu
    const onContextMenu = useCallback(
      (event) => {
        event.preventDefault();
        const bounds = event.currentTarget.getBoundingClientRect();
        setContextMenu({
          x: event.clientX - bounds.left,
          y: event.clientY - bounds.top,
          hasSelection: nodes.some((n) => n.selected) || edges.some((e) => e.selected)
        });
      },
      [nodes, edges]
    );

    const onPaneClick = useCallback(() => {
      setSelectedNodeId(null);
      setSelectedEdgeId(null);
      setSelectedEdge(null);
      setContextMenu(null);
    }, []);

    const onNodeClick = useCallback((event, node) => {
      setSelectedEdgeId(null);
      setSelectedEdge(null);
      setSelectedNodeId((prev) => (prev === node.id ? null : node.id));
    }, []);

    const styledNodes = useMemo(() => {
      return nodes.map((node) => ({
        ...node,
        style: {
          ...node.style,
          borderRadius: node.id === selectedNodeId ? '10px' : ''
        }
      }));
    }, [nodes, selectedNodeId]);

    const styledEdges = edges.map(
      (edge) => {
        const selectedColor = theme ? '#ffffff' : '#000000';
        const isSelected = edge.id === selectedEdgeId;
        return {
          ...edge,
          style: {
            ...(edge.style || {}),
            stroke: isSelected ? selectedColor : edge.style?.stroke || '#a78bfa',
            strokeWidth: isSelected ? 3 : edge.style?.strokeWidth || 2,
            strokeDasharray: edge.style?.strokeDasharray || 'none'
          },
          markerEnd: {
            ...(edge.markerEnd || {}),
            color: isSelected ? selectedColor : edge.markerEnd?.color || '#a78bfa'
          }
        };
      },
      [edges, selectedEdgeId]
    );

    const onNodeDragStop = useCallback(
      (_, draggedNode) => {
        const updatedNodes = handleNodeDragStop(nodes, draggedNode);
        setNodes(updatedNodes);
      },
      [nodes]
    );

    const handleNodeUnGroup = useCallback((groupId) => {
      setNodes((prevNodes) => handleUngroupNode(prevNodes, groupId));
    }, []);

    const handleDetachNode = useCallback((id) => {
      setNodes((nds) => handleNodeDetach(nds, id));
    }, []);

    const enhancedNodes = useMemo(() => {
      return styledNodes.map((node) => {
        const baseData = {
          ...node.data,
          onDetach: node.type === 'group' ? handleNodeUnGroup : handleDetachNode
        };

        if (node.type !== 'group') {
          baseData.parentId = node.parentId;
        }

        return {
          ...node,
          data: baseData
        };
      });
    }, [styledNodes, handleDetachNode, handleNodeUnGroup]);

    return (
      <div style={{ width: '100%', height: '100%', position: 'relative' }} onContextMenu={onContextMenu}>
        <ReactFlow
          nodes={enhancedNodes}
          edges={styledEdges}
          onConnect={onConnect}
          colorMode={theme ? 'dark' : 'light'}
          onNodesChange={onNodesChange}
          onNodeClick={onNodeClick}
          onEdgesChange={onEdgesChange}
          onNodeDoubleClick={onNodeDoubleClick}
          onNodeDragStop={onNodeDragStop}
          onEdgeClick={handleEdgeClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          fitViewOptions={{ includeHiddenNodes: true, padding: 4 }}
          deleteKeyCode={['Delete', 'Backspace']}
          selectionKeyCode="Shift"
          connectionMode="strict"
          defaultEdgeOptions={{
            type: 'custom',
            animated: true
          }}
          connectionLineStyle={{
            stroke: '#cdcdcd',
            strokeWidth: 2,
            strokeDasharray: isConditionalEdge ? '5,5' : 'none'
          }}
          connectionLineType="default"
          connectionRadius={55}
          onPaneClick={onPaneClick}
        >
          <Controls>
            <ControlButton onClick={toggleFullscreen} title={isFullScreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}>
              {isFullScreen ? <CloseFullscreen /> : <OpenInFull />}
            </ControlButton>
          </Controls>
          <Background />
        </ReactFlow>

        {selectedEdge && (
          <EditEdgeModal
            isOpen={isEdgeModalOpen}
            onClose={() => setIsEdgeModalOpen(false)}
            edgeData={selectedEdge.data}
            onSave={handleEdgeUpdate}
          />
        )}

        {contextMenu && (
          <div
            style={{
              ...contextMenuStyles.container,
              left: contextMenu.x,
              top: contextMenu.y
            }}
          >
            <button
              onClick={() => {
                deleteSelectedElements();
                setContextMenu(null);
              }}
              disabled={!contextMenu.hasSelection}
              style={contextMenu.hasSelection ? contextMenuStyles.button : contextMenuStyles.buttonDisabled}
            >
              Delete Selected
            </button>
            {/* <button
            onClick={() => {
              setNodes([]);
              setEdges([]);
              setContextMenu(null);
            }}
            style={contextMenuStyles.button}
          >
            Clear All
          </button> */}
          </div>
        )}
      </div>
    );
  }
);

// Add this component for the graph list popup

const GraphListPopup = ({ onClose, onSelect, tenantId, handleReset, currentGraphId, canEditGraph, setVersionList }) => {
  const [graphs, setGraphs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [isLoadingAfterDelete, setLoadingAfterDelete] = useState(false);
  const [error, setError] = useState(null);
  const [convertedCode, setConvertedCode] = useState(null);
  const [deletingGraph, setDeletingGraph] = useState({});
  const codeSectionRef = useRef(null);
  const [selectedVersions, setSelectedVersions] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [pendingDefault, setPendingDefault] = useState(null);
  const [isSettingDefault, setIsSettingDefault] = useState(false);

  const filteredGraphs = useMemo(() => {
    if (!searchQuery) return graphs;
    const query = searchQuery.toLowerCase();
    return graphs.filter((g) => g.name?.toLowerCase().includes(query) || g.graphId?.toLowerCase().includes(query));
  }, [graphs, searchQuery]);

  useEffect(() => {
    getGraphs();
  }, [tenantId]);

  useEffect(() => {
    const newSelectedVersions = {};

    const grouped = graphs.reduce((acc, g) => {
      if (!acc[g.graphId]) acc[g.graphId] = [];
      acc[g.graphId].push(g);
      return acc;
    }, {});

    Object.entries(grouped).forEach(([graphId, versions]) => {
      const defaultVersion = versions.find((v) => v.isDefault) || versions[0];
      newSelectedVersions[graphId] = defaultVersion;
    });

    setSelectedVersions(newSelectedVersions);
  }, [graphs]);

  async function getGraphs() {
    const token = utils.getToken();
    try {
      const response = await axios.get(`${env.EVERGREEN_API_BASE_URL}/graphs`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          [constants.BERLIN_TENANT_ID_HEADER]: tenantId
        }
      });
      const data = response.data;
      if (data.success) {
        setGraphs(data.graphs);
        globalGraphs = data.graphs;
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError('Failed to load graph list');
    } finally {
      setLoading(false);
      setLoadingAfterDelete(false);
    }
  }

  const handleSearch = (event) => {
    const query = event.target.value?.toLowerCase();
    setSearchQuery(query);
    const filtered = graphs.filter(
      (graph) => graph.name?.toLowerCase().includes(query) || graph.graphId?.toLowerCase().includes(query)
    );
  };

  const handleConvert = async (graphId, currentVersion) => {
    try {
      const token = utils.getToken();
      // filename = filename.replace(/\.json$/, '');
      const response = await axios.get(`${env.EVERGREEN_API_BASE_URL}/code/${graphId}?version=${currentVersion}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'x-berlin-tenant-id': tenantId
        }
      });
      const data = response.data;
      if (data.success) {
        setConvertedCode(data.code);
        setTimeout(() => {
          codeSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 0);
      } else {
        const notification = {
          open: true,
          type: 'error',
          message: `Error while showing graph: ${data.message}`
        };
        Framework.getStoreRegistry().getStore('notificationState').set(notification);
      }
    } catch (error) {
      console.error('Error Showing graph:', error);
      const notification = {
        open: true,
        type: 'error',
        message: 'Failed to show graph. Please try again.'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
    }
  };

  const handleDeleteGraph = async (graphId, currentVersion) => {
    const token = utils.getToken();
    const graphKey = `${graphId}_${currentVersion}`;
    setDeletingGraph((prev) => ({ ...prev, [graphKey]: true }));
    setLoadingAfterDelete(true);
    try {
      await axios.delete(
        `${env.EVG_SVC_URL}${env.EVERGREEN_API_BASE_URL}/graphs/${graphId}?version=${currentVersion}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-berlin-tenant-id': tenantId
          }
        }
      );
      const notification = {
        open: true,
        type: 'success',
        message: `Graph deleted successfully!`
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      await getGraphs();
    } catch (error) {
      console.error('Error deleting graph:', error.response?.data || error.message);
      const notification = {
        open: true,
        type: 'error',
        message: `Failed to delete. Please try again.`
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      setLoadingAfterDelete(false);
    } finally {
      setDeletingGraph((prev) => ({ ...prev, [graphKey]: false }));
      setSearchQuery('');
      if (currentGraphId === graphId) {
        handleReset();
      }
    }
  };

  const handleSetAsDefault = async (graphId, version) => {
    const token = utils.getToken();
    setIsSettingDefault(true);

    const endpoint = '/graphs';
    const methodType = 'PATCH';

    const payload = {
      operation: 'modify',
      criteria: {
        graphId,
        version
      },
      properties: {
        isDefault: true
      }
    };

    try {
      const response = await axios({
        method: methodType,
        url: `${env.EVG_SVC_URL}${env.EVERGREEN_API_BASE_URL}${endpoint}`,
        data: payload,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'x-berlin-tenant-id': tenantId
        }
      });

      if (response.data.success) {
        Framework.getStoreRegistry()
          .getStore('notificationState')
          .set({
            open: true,
            type: 'success',
            message: response?.data.data ? response?.data.data : 'Success'
          });

        setGraphs((prev) => prev.map((g) => (g.graphId === graphId ? { ...g, isDefault: g.version === version } : g)));

        await getGraphs();
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      Framework.getStoreRegistry()
        .getStore('notificationState')
        .set({
          open: true,
          type: 'error',
          message: `Failed to set default: ${err.message}`
        });
    } finally {
      setIsSettingDefault(false);
      setShowModal(false);
      setPendingDefault(null);
    }
  };

  const popupStyle = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    maxWidth: convertedCode ? '80%' : '600px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
    zIndex: 1000
  };

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 999,
    ...(isLoadingAfterDelete || isSettingDefault ? { pointerEvents: 'auto' } : {})
  };

  const buttonStyle = {
    padding: '8px 6px',
    margin: '5px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    cursor: 'pointer',
    backgroundColor: '#f0f0f0',
    transition: 'background-color 0.2s'
  };

  const disabledStyle = {
    opacity: 0.8,
    cursor: 'not-allowed'
  };

  const headerContainerStyle = {
    display: 'flex',
    justifyContent: 'space-between'
  };

  const headerTitleStyle = {
    marginTop: 0
  };

  const errorStyle = {
    color: 'red'
  };

  const graphItemStyle = {
    padding: '10px',
    margin: '5px 0',
    borderRadius: '4px',
    border: '1px solid #eee',
    transition: 'background-color 0.2s'
  };

  const graphHeaderStyle = {
    margin: '0 0 5px 0'
  };

  const graphIdStyle = {
    display: 'block',
    color: '#555',
    marginBottom: '6px'
  };

  const graphInfoRowStyle = {
    display: 'flex',
    gap: '16px',
    marginBottom: '8px',
    justifyContent: 'space-between'
  };

  const selectStyle = {
    marginLeft: '5px',
    padding: '2px'
  };

  const graphButtonStyles = {
    marginTop: '10px',
    display: 'flex',
    flexWrap: 'wrap'
  };

  const codeStyle = {
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
    padding: '15px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    overflow: 'auto',
    maxHeight: '60vh'
  };

  const searchInputStyle = {
    width: '44%',
    padding: '8px',
    marginBottom: '15px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '14px'
  };

  const loadGraphButtonStyle = {
    ...buttonStyle,
    marginRight: '10px'
  };

  const convertButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#4ade80'
  };

  const deleteButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#FF4C4C',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const loaderStyle = {
    border: '2px solid #f3f3f3',
    borderTop: '2px solid #FF4C4C',
    borderRadius: '50%',
    width: '12px',
    height: '12px',
    animation: 'spin 1s linear infinite',
    display: 'inline-block',
    marginLeft: '8px'
  };

  const codeSectionContainerStyle = {
    marginTop: '20px'
  };

  const footerStyle = {
    marginTop: '20px',
    textAlign: 'right'
  };

  const disabledGraphStyle = {
    pointerEvents: 'none',
    opacity: '0.5'
  };

  const styleSheet = document.styleSheets[0];
  styleSheet.insertRule(
    `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `,
    styleSheet.cssRules.length
  );

  const groupedGraphs = useMemo(() => {
    return filteredGraphs.reduce((acc, g) => {
      if (!acc[g.graphId]) acc[g.graphId] = [];
      acc[g.graphId].push(g);
      return acc;
    }, {});
  }, [filteredGraphs]);

  // Sort versions latest first
  Object.values(groupedGraphs).forEach((group) => group.sort((a, b) => b.version - a.version));

  return (
    <div>
      <div style={overlayStyle} onClick={onClose} />
      <div style={popupStyle}>
        {(isLoadingAfterDelete || isSettingDefault) && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(255, 255, 255, 0.8)',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2000
            }}
          >
            <span>Processing...</span>
          </div>
        )}
        <div style={headerContainerStyle}>
          <h2 style={headerTitleStyle}>Load Graph</h2>
          <input
            type="text"
            placeholder="Search graphs by name or Id..."
            value={searchQuery}
            onChange={handleSearch}
            style={searchInputStyle}
          />
        </div>
        {loading ? (
          <p>Loading graphs...</p>
        ) : error ? (
          <p style={errorStyle}>{error}</p>
        ) : filteredGraphs.length === 0 ? (
          <p>No graphs found</p>
        ) : (
          <div>
            {Object.entries(groupedGraphs).map(([graphId, versions]) => {
              const selectedVersion = selectedVersions[graphId] || versions[0]; // latest by default

              return (
                <div key={graphId} style={{ ...graphItemStyle, ...(isLoadingAfterDelete ? disabledGraphStyle : {}) }}>
                  <h3 style={graphHeaderStyle}>{selectedVersion.name}</h3>
                  <small style={graphIdStyle}>Id: {graphId}</small>
                  <div style={graphInfoRowStyle}>
                    <small>Last modified: {new Date(selectedVersion.lastModified).toLocaleString()}</small>
                    <small>
                      Version:
                      <select
                        value={selectedVersion.version}
                        onChange={(e) => {
                          const newVer = versions.find((v) => v.version === Number(e.target.value));
                          setSelectedVersions((prev) => ({ ...prev, [graphId]: newVer }));
                        }}
                        style={selectStyle}
                      >
                        {versions.map((v) => (
                          <option key={v.version} value={v.version}>
                            {`${v.version} - ${v.lifeCycleStatus}`} {v.isDefault}
                          </option>
                        ))}
                      </select>
                    </small>
                    {selectedVersion?.isDefault ? <Chip label="Default" /> : <div>&nbsp;</div>}
                  </div>

                  {/* Action buttons */}
                  <div style={graphButtonStyles}>
                    <button
                      style={loadGraphButtonStyle}
                      onClick={() => {
                        const versionObj = versions.map((version) => ({
                          lifeCycleStatus: version.lifeCycleStatus,
                          version: `${version.version}`
                        }));
                        setVersionList(versionObj);
                        onSelect(selectedVersion.graphId, false, selectedVersion.version);
                      }}
                    >
                      Load Graph
                    </button>
                    <button
                      style={convertButtonStyle}
                      onClick={() => handleConvert(selectedVersion.graphId, selectedVersion.version)}
                    >
                      Show LangGraph Code
                    </button>
                    <div>
                      <button
                        style={{
                          ...deleteButtonStyle,
                          ...(!canEditGraph ? disabledStyle : { cursor: 'pointer' })
                        }}
                        onClick={() => handleDeleteGraph(selectedVersion.graphId, selectedVersion.version)}
                        disabled={
                          !canEditGraph && deletingGraph[`${selectedVersion.graphId}_${selectedVersion.version}`]
                        }
                      >
                        {deletingGraph[`${selectedVersion.graphId}_${selectedVersion.version}`] ? (
                          <span style={loaderStyle}></span>
                        ) : (
                          'Delete Graph'
                        )}
                      </button>
                    </div>
                    {selectedVersion?.lifeCycleStatus === 'LIVE' && !selectedVersion?.isDefault && (
                      <div>
                        <button
                          style={{
                            ...convertButtonStyle,
                            ...(!canEditGraph ? disabledStyle : { cursor: 'pointer' })
                          }}
                          onClick={() => {
                            setPendingDefault(selectedVersion);
                            setShowModal(true);
                          }}
                          disabled={!canEditGraph}
                        >
                          Set as Default
                        </button>

                        {showModal && pendingDefault && (
                          <Confirmation
                            open
                            callback={(action) => {
                              if (action) {
                                handleSetAsDefault(pendingDefault.graphId, pendingDefault.version);
                              }
                              setShowModal(false);
                            }}
                            message={
                              pendingDefault.defaultVersion
                                ? `Are you sure you want to set this LIVE version as default? Current default LIVE Version: ${pendingDefault.defaultVersion}`
                                : `Are you sure you want to set this LIVE version as default?`
                            }
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {convertedCode && (
          <div ref={codeSectionRef} style={codeSectionContainerStyle}>
            <h3>Generated LangGraph Code:</h3>
            <div style={codeStyle}>{convertedCode}</div>
          </div>
        )}
        <div style={footerStyle}>
          <button style={buttonStyle} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const GraphEditor = (props) => {
  const { userPermissions, userPermissionCheck, tenantState } = props;
  const { providerList } = tenantState;

  const [nodes, setNodes] = useState(initialNodes);
  const [edges, setEdges] = useState(initialEdges);
  const [isConditionalEdge, setIsConditionalEdge] = useState(false);
  const [currentGraphName, setCurrentGraphName] = useState(null);
  const [currentGraphId, setCurrentGraphId] = useState(null);
  const [isTesterOpen, setIsTesterOpen] = useState(false);
  const reactFlowWrapper = useRef(null);
  const nodesRef = useRef(nodes); // Add this ref to store current nodes
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [promptTypes, setPromptTypes] = useState([]);
  const [bookDataList, setBookDataList] = useState([]);
  const [saveType, setSaveType] = useState('');
  const [fetchedGraphId, setFetchedGraphId] = useState('');
  const [newGraphName, setNewGraphName] = useState('');
  // State for the edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [nodeTypeToEdit, setNodeTypeToEdit] = useState('');
  const [showGraphList, setShowGraphList] = useState(false);
  const [currentGraphVersion, setCurrentGraphVersion] = useState(0);
  const [currentLiveVersionList, setCurrentLiveVersionList] = useState([]);
  const [currentGraphStatus, setCurrentGraphStatus] = useState('');
  const [selectedDefaultVersion, setSelectedDefaultVersion] = useState(false);
  const [defaultVersion, setDefaultVersion] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [groupCount, setGroupCount] = useState(0);
  const containerRef = useRef(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [versionList, setVersionList] = useState(null);
  const { handleGroupZIndices, handleGroupToggle } = useNodeActions();
  const permissionsList = toJS(userPermissions.permissionList.permissions);
  const isAdminUser = !userPermissionCheck?.isPermissionCheck && permissionsList.includes(GRAPH_ADMIN);

  const clearSelection = () => {
    // Optional: Clear selection after group creation
    setTimeout(() => {
      setNodes((nds) => nds.map((n) => ({ ...n, selected: false })));
    }, 0);
  };

  useEffect(() => {
    setNodes((currentNodes) => {
      return currentNodes.map((node, index) => {
        if (node.type === 'group') {
          const colorIndex = index % (isDarkMode ? groupColors.length : groupColorsLight.length);
          const color = isDarkMode ? groupColors[colorIndex] : groupColorsLight[colorIndex];
          return {
            ...node,
            style: {
              ...node.style,
              background: color,
              border: `2px solid ${color}`
            }
          };
        }
        return node;
      });
    });
  }, [isDarkMode, setNodes]);

  useEffect(() => {
    nodesRef.current = nodes;
    handleGroupZIndices(nodes, setNodes);
  }, [nodes, handleGroupZIndices]);

  useEffect(() => {
    const tokenValue = utils.getToken();
    const isCDNtokenPresent = utils.getCookieByName('etext-cdn-token');
    const headers = {
      Authorization: `Bearer ${tokenValue}`
    };

    const getCDNTokenData = async () => {
      const response = await axios.get(`${env.EVERGREEN_API_BASE_URL}${constants.GET_CDN_TOKEN_URL}`, { headers });
      if (response && response.data && response.data.data) {
        const cdnTokenVal = response.data.data;
        utils.setCDNTokenInCookie(cdnTokenVal);
      }
    };
    if (!isCDNtokenPresent) {
      getCDNTokenData();
    }

    // Listener on tenantList api success
    Framework.getEventManager().on(serviceConstants.GET_TENANT_LIST_SUCCESS, () => {
      const { tenantList } = tenantState;
      if (selectedTenantId === '' && tenantList && tenantList.length > 0) {
        // Auto-select the default  tenant by name
        const defaultTenant = tenantList.find((t) => t.name === DEFAULT_TENANT_NAME);
        if (defaultTenant) {
          const tenantId = defaultTenant.tenantId;
          setSelectedTenantId(tenantId);
          tenantState.setSelectedTenant(defaultTenant.name);
        }
      }
    });
  }, [tenantState.tenantList]);

  useEffect(() => {
    const tokenValue = utils.getToken();
    const getBookData = async () => {
      // Get tenant name first before calling the API
      const { tenantList } = tenantState;
      const selectedTenant = tenantList?.find((t) => t.tenantId === selectedTenantId);
      const tenantName = selectedTenant?.name;

      // Set autobahn to true only when tenant name is PVS, false for all others
      const isAutobahnEnabled = tenantName === constants.PVS;

      // Build headers using utility method
      const tenantHeaders = utils.buildHeadersWithTenantId({ tenantId: selectedTenantId });
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokenValue}`,
        ...tenantHeaders
      };

      const response = await axios.get(
        `${env.AI_ADMIN_SERVICES}/api/v1/tenant/identifiers?autobahn=${isAutobahnEnabled}`,
        { headers }
      );
      if (response && response.data && response.data.data) {
        const bookData = response?.data?.data;

        if (bookData && Array.isArray(bookData)) {
          // const bookOptions = bookData.map((book) => `${book.title} (${book.bookId})`);
          setBookDataList(bookData);
        }
      }
    };
    const getSysTosysToken = async (tenantId) => {
      const sysTosysToken = await TokenService.getSys2sysToken();
      Framework.getEventManager().publish(serviceConstants.GET_TENANT_PROVIDER_LIST, {
        tenantId,
        sysTosysToken
      });
    };
    if (selectedTenantId) {
      getBookData();
      getSysTosysToken(selectedTenantId);
    }
  }, [selectedTenantId]);

  useEffect(() => {
    return () => {
      handleReset();
    };
  }, [selectedTenantId]);

  useEffect(() => {
    const tokenValue = utils.getToken();
    const getPromptTypeData = async () => {
      const headers = {
        Authorization: `Bearer ${tokenValue}`,
        [constants.BERLIN_TENANT_ID_HEADER]: selectedTenantId
      };
      const response = await axios.post(
        `${env.EVERGREEN_API_BASE_URL}/chatutility/admin/getTypes`,
        {
          isDevTest: false
        },
        { headers }
      );
      if (response && response.data && response.data.data) {
        const promptTypes = response?.data?.data;

        if (promptTypes && Array.isArray(promptTypes)) {
          const uniqueTypes = [...new Set(promptTypes)];
          if (uniqueTypes && uniqueTypes.length > 0) {
            setPromptTypes(uniqueTypes);
          }
          // const uniqueProviders = Array.from(new Set(filteredProviders));

          // setProviderOptions(uniqueProviders);

          // ✅ Auto-select if only one provider
          // if (uniqueProviders.length === 1) {
          //   setFormData((prev) => ({
          //     ...prev,
          //     modelProvider: uniqueProviders[0]
          //   }));
          // }
        }
      }
    };
    if (selectedTenantId) {
      getPromptTypeData();
    }
  }, [selectedTenantId]);

  // Create stable references to state setter functions
  const setNodesStable = useCallback((updater) => {
    setNodes(updater);
  }, []);

  const setEdgesStable = useCallback((updater) => {
    setEdges(updater);
  }, []);

  // Stable double-click handler that uses nodesRef instead of nodes
  const handleNodeDoubleClick = useCallback((nodeId, nodeType, nodeData) => {
    const node = nodesRef.current.find((n) => n.id === nodeId);
    if (node) {
      setSelectedNode(node);
      setNodeTypeToEdit(nodeType);
      setModalOpen(true);
    }
    const nodeItem = nodesRef.current.find((n) => n.id === nodeType?.id);
    if (nodeItem) {
      handleConfigureNodeClick(nodeItem);
    }
  }, []); // No dependencies needed now

  const handleReset = () => {
    setNodes(initialNodes);
    setEdges(initialEdges);
    setIsConditionalEdge(false);
    setCurrentGraphName(null);
    setCurrentGraphId(null);
    setIsTesterOpen(false);
    setModalOpen(false);
    setSelectedNode(null);
    setNodeTypeToEdit('');
    setShowGraphList(false);
    setShowPopup(false);
    setNewGraphName('');
    setFetchedGraphId('');
    setSaveType('');
    nodesRef.current = initialNodes;
  };

  const handleNewGraph = () => {
    handleReset();
    const notification = {
      open: true,
      type: 'info',
      message: 'New agent graph created. Start adding your nodes.'
    };
    Framework.getStoreRegistry().getStore('notificationState').set(notification);
  };

  // Update nodes with the double-click handler only when handleNodeDoubleClick changes
  useEffect(() => {
    setNodesStable((currentNodes) =>
      currentNodes.map((node) => {
        if (
          (node.type === 'agent' || node.type === 'tool') &&
          (!node.data.onNodeDoubleClick || node.data.onNodeDoubleClick !== handleNodeDoubleClick)
        ) {
          return {
            ...node,
            data: {
              ...node.data,
              onNodeDoubleClick: handleNodeDoubleClick
            }
          };
        }
        return node;
      })
    );
  }, [handleNodeDoubleClick, setNodesStable]);

  // Handle saving updates from the modal
  const handleSaveNodeChanges = useCallback(
    (updatedData) => {
      if (!selectedNode) return;

      setNodesStable((currentNodes) => {
        return currentNodes.map((node) => {
          if (node.id === selectedNode.id) {
            return {
              ...node,
              data: {
                ...updatedData,
                onNodeDoubleClick: handleNodeDoubleClick
              }
            };
          }
          return node;
        });
      });

      setModalOpen(false);
    },
    [selectedNode, setNodesStable, handleNodeDoubleClick]
  );

  // Handler for the Configure Selected Node button
  const handleConfigureNodeClick = useCallback(
    (nodeData) => {
      // Get the current nodes directly from the state
      const selectedNodes = nodeData?.data ? [nodeData] : nodes.filter((node) => node.selected);

      if (!selectedNodes.length) {
        // alert('Please select a node to configure');
        const notification = {
          open: true,
          type: 'error',
          message: 'Please select a node to configure'
        };
        Framework.getStoreRegistry().getStore('notificationState').set(notification);
        return;
      }

      // Take the first selected node
      const selectedNode = selectedNodes[0];

      if (selectedNode.type === 'agent' || selectedNode.type === 'tool' || selectedNode.type === 'supervisor') {
        setSelectedNode(selectedNode);
        setNodeTypeToEdit(selectedNode.type);
        setModalOpen(true);
      } else {
        const notification = {
          open: true,
          type: 'error',
          message: `Cannot configure ${selectedNode.type} node`
        };
        Framework.getStoreRegistry().getStore('notificationState').set(notification);
        // alert(`Cannot configure ${selectedNode.type} node`);
      }
    },
    [nodes]
  );

  // Handler to add a new node based on the given node type
  const handleAddNode = (nodeType) => {
    if (nodeType === 'group') {
      handleGroupToggle(nodes, setNodes, groupCount, setGroupCount, clearSelection, isDarkMode);
      return;
    }
    // For START and END nodes, ensure only one exists
    if ((nodeType === 'start' || nodeType === 'end') && nodes.some((n) => n.type === nodeType)) {
      const notification = {
        open: true,
        type: 'error',
        message: `${nodeType.toUpperCase()} node already exists.`
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      // alert(`${nodeType.toUpperCase()} node already exists.`);
      return;
    }

    // Calculate next unique node id
    let id = '';
    let data = {};

    // Generate a unique identifier for nodes
    const timestamp = Date.now();
    const randomId = utils.uuid();

    switch (nodeType) {
      case 'start':
        id = 'start';
        data = { label: '__start__' };
        break;
      case 'end':
        id = 'end';
        data = { label: '__end__' };
        break;
      case 'agent':
        // Count existing agent nodes to create incremental ID
        const agentCount = nodes.filter((n) => n.type === 'agent').length + 1;
        id = `agent_${agentCount}_${timestamp.toString().slice(-4)}`;
        data = {
          prompt: `agent${agentCount}`,
          agentId: id, // Use the same ID for consistency
          // The double-click handler will be added by the useEffect
          // LangGraph agent configuration properties
          config: {
            // Model configuration
            model: {
              provider: 'openai',
              model: 'gpt-4o',
              temperature: 0.7,
              maxTokens: 1000,
              promptType: ''
            },
            // System prompt for the agent
            systemPrompt: `You are agent ${agentCount}, a super helpful teacher that explains student questions step by step. You should explain for grade 5 students. Always try to cheer the student up and make them feel good.`,
            // Message template for formatting
            template: '{{input}}',
            // Memory configuration
            memory: {
              type: 'buffer', // Options: buffer, summary, none
              maxMessages: 10,
              reflectionThreshold: 5
            },
            // Tool calling configuration
            toolCalling: {
              enabled: true,
              toolChoice: 'auto' // Options: auto, any, none
            },
            // Function definitions (empty by default)
            functions: [],
            // Agent state
            state: {
              variables: [] // List of state variables the agent can access/modify
            },
            parsers: []
          }
        };
        break;
      case 'tool':
        // Count existing tool nodes to create incremental ID
        const toolCount = nodes.filter((n) => n.type === 'tool').length + 1;
        id = `tool_${toolCount}_${timestamp.toString().slice(-4)}`;
        data = {
          tool_name: `tools${toolCount}`,
          toolId: randomId,
          // The double-click handler will be added by the useEffect
          // LangGraph tool configuration properties
          config: {
            // Tool function properties
            name: `tool_${toolCount}`,
            description: 'This tool performs addition of two numbers.',
            // Function parameters in JSON schema format
            parameters: {
              type: 'object',
              properties: {
                query: {
                  type: 'string',
                  description: 'The input parameter for this tool'
                }
              },
              required: ['query']
            },
            // Implementation details
            implementation: {
              type: 'function', // Options: function, api, langchain
              // For API implementation
              endpoint: '',
              method: 'POST',
              authType: 'None',
              headers: {},
              // For code implementation (simplified template)
              code: 'async (params) => {\n  // Sample code and param can be modified if needed.\n  const result = await customMethod(params);\n  return result;\n}',
              // Response format
              outputFormat: {
                type: 'object',
                properties: {
                  result: {
                    type: 'string',
                    description: 'The result of the tool execution'
                  }
                }
              }
            },
            // Access control
            accessControl: {
              requiresAuth: false,
              rateLimit: 100
            }
          }
        };
        break;
      case 'supervisor':
        // Count existing agent nodes to create incremental ID
        const supervisorCount = nodes.filter((n) => n.type === 'supervisor').length + 1;
        id = `supervisor_${supervisorCount}_${timestamp.toString().slice(-4)}`;
        data = {
          prompt: `supervisor${supervisorCount}`,
          supervisorId: id, // Use the same ID for consistency
          // The double-click handler will be added by the useEffect
          // LangGraph supervisor configuration properties
          config: {
            // Model configuration
            model: {
              provider: 'openai',
              model: 'gpt-4o',
              temperature: 0.7,
              maxTokens: 1000,
              promptType: ''
            },
            // System prompt for the supervisor
            systemPrompt: `You are supervisor ${supervisorCount}, You are a routing and output agent. Given the user's last message, carefully analyse and execute only those agents and give the output as is from the agents and in a clear and separated manner and do not mix the outputs from different agents. Also do not generate anything from your end STRICTLY. In the end just print the name of each agent that were used in individual brackets.`,
            // Message template for formatting
            template: '{{input}}',
            // Memory configuration
            memory: {
              type: 'buffer', // Options: buffer, summary, none
              maxMessages: 10,
              reflectionThreshold: 5
            },
            // Tool calling configuration
            toolCalling: {
              enabled: false,
              toolChoice: 'auto' // Options: auto, any, none
            },
            // Function definitions (empty by default)
            functions: [],
            // Agent state
            state: {
              variables: [] // List of state variables the supervisor can access/modify
            }
          },
          // Add agentConfigs to store connected agent configurations
          agentConfigs: []
        };
        break;

      default:
        id = `node_${nodes.length + 1}_${timestamp}`;
        data = { label: 'Node' };
    }

    // Get the current viewport center and bounds
    const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
    const centerX = reactFlowBounds.width / 2;
    const centerY = reactFlowBounds.height / 2;

    // Default position
    let position = { x: 0, y: 0 };

    // Smart positioning based on node type and existing nodes
    // if (nodeType === 'start') {
    //   // Start node at the top center
    //   position = { x: centerX - 75, y: 50 };
    // } else if (nodeType === 'end') {
    //   // End node at the bottom center
    //   position = { x: centerX - 75, y: centerY + 200 };
    // } else if (nodeType === 'agent') {
    //   // Get all existing agent nodes
    //   const existingAgents = nodes.filter((n) => n.type === 'agent');

    //   if (existingAgents.length === 0) {
    //     // First agent goes in the center
    //     position = { x: centerX - 75, y: centerY - 75 };
    //   } else {
    //     // Count agents in middle row
    //     const middleRowAgents = existingAgents.filter(
    //       (n) => n.position.y >= centerY - 150 && n.position.y <= centerY + 50
    //     );

    //     // If middle row has less than 3 agents, add to middle row
    //     if (middleRowAgents.length < 3) {
    //       position = {
    //         x: centerX - 75 + middleRowAgents.length * 200,
    //         y: centerY - 75
    //       };
    //     } else {
    //       // Add to a new row above or below
    //       const topRowAgents = existingAgents.filter((n) => n.position.y < centerY - 150);
    //       const bottomRowAgents = existingAgents.filter((n) => n.position.y > centerY + 50);

    //       if (topRowAgents.length <= bottomRowAgents.length) {
    //         // Add to top row
    //         position = {
    //           x: centerX - 75 + ((topRowAgents.length * 200) % (reactFlowBounds.width - 300)),
    //           y: centerY - 250
    //         };
    //       } else {
    //         // Add to bottom row
    //         position = {
    //           x: centerX - 75 + ((bottomRowAgents.length * 200) % (reactFlowBounds.width - 300)),
    //           y: centerY + 100
    //         };
    //       }
    //     }
    //   }
    // } else if (nodeType === 'tool') {
    //   // Tools go on the left side
    //   const existingTools = nodes.filter((n) => n.type === 'tool');

    //   if (existingTools.length === 0) {
    //     // First tool goes on the left middle
    //     position = { x: 150, y: centerY - 75 };
    //   } else {
    //     // Stack tools vertically
    //     const lastTool = existingTools[existingTools.length - 1];
    //     const yPos = lastTool.position.y + 150;

    //     // If we'd go off the bottom, create a new column
    //     if (yPos > reactFlowBounds.height - 150) {
    //       const columnCount = Math.floor(existingTools.length / 3) + 1;
    //       position = {
    //         x: 150 + (columnCount - 1) * 200,
    //         y: centerY - 225 + (existingTools.length % 3) * 150
    //       };
    //     } else {
    //       position = { x: lastTool.position.x, y: yPos };
    //     }
    //   }
    // } else if (nodeType === 'supervisor') {
    //   // Get all existing supervisor nodes
    //   const existingSupervisor = nodes.filter((n) => n.type === 'supervisor');

    //   if (existingSupervisor.length === 0) {
    //     // First supervisor goes in the center
    //     position = { x: centerX - 75, y: centerY - 75 };
    //   } else {
    //     // Count supervisors in middle row
    //     const middleRowAgents = existingSupervisor.filter(
    //       (n) => n.position.y >= centerY - 150 && n.position.y <= centerY + 50
    //     );

    //     // If middle row has less than 3 agents, add to middle row
    //     if (middleRowAgents.length < 3) {
    //       position = {
    //         x: centerX - 75 + middleRowAgents.length * 200,
    //         y: centerY - 75
    //       };
    //     } else {
    //       // Add to a new row above or below
    //       const topRowAgents = existingSupervisor.filter((n) => n.position.y < centerY - 150);
    //       const bottomRowAgents = existingSupervisor.filter((n) => n.position.y > centerY + 50);

    //       if (topRowAgents.length <= bottomRowAgents.length) {
    //         // Add to top row
    //         position = {
    //           x: centerX - 75 + ((topRowAgents.length * 200) % (reactFlowBounds.width - 300)),
    //           y: centerY - 250
    //         };
    //       } else {
    //         // Add to bottom row
    //         position = {
    //           x: centerX - 75 + ((bottomRowAgents.length * 200) % (reactFlowBounds.width - 300)),
    //           y: centerY + 100
    //         };
    //       }
    //     }
    //   }
    // }

    // Create a new node
    const newNode = {
      id,
      type: nodeType,
      data,
      position,
      draggable: true,
      selectable: true,
      style: {
        // Add shadow effect when dragging
        boxShadow: '0 4px 6px rgba(128, 105, 105, 0.1)',
        // transition: 'box-shadow 0.3s, transform 0.1s',
        cursor: 'move'
      }
    };

    setNodesStable((nds) => [...nds, newNode]);
  };

  const handleCallSaveGraph = async (name, userComment = '', isDefault = false, defaultVersionVal = '') => {
    let graphName = name || newGraphName || currentGraphName;
    if (!graphName) {
      setShowPopup(true);
      return;
    }

    const token = utils.getToken();
    const transformedNodes = nodes.map(transformNode);
    const transformedEdges = edges.map((edge) => transformEdge(edge, nodes));

    const graphData = {
      tenantId: selectedTenantId,
      nodes: transformedNodes,
      edges: transformedEdges
    };

    // when we are doing publish which draft to live and have some changes then
    const saveNPublishCheck = currentGraphStatus === constants.PROMPT_DRAFT_STATUS && saveType === 'publishChanges';
    const liveStatusCheck = currentGraphStatus === constants.PROMPT_LIVE_STATUS;

    const isUpdate = !!currentGraphId && saveType !== 'copy' && !liveStatusCheck;
    const endpoint = '/graphs';
    const methodType = isUpdate ? 'PUT' : 'POST';

    const version = currentGraphVersion;

    const payload = {
      graphName,
      graphData,
      // version,
      ...(isUpdate || liveStatusCheck ? { version } : {}),
      ...(isUpdate || liveStatusCheck ? { graphId: currentGraphId } : {}),
      ...(liveStatusCheck ? { lifeCycleStatus: currentGraphStatus } : {}),
      ...(liveStatusCheck || saveNPublishCheck ? { comment: userComment } : {}),
      ...(saveNPublishCheck ? { type: 'PUBLISH' } : {}), // only when we have PUBLISH
      ...(isDefault ? { isDefault: true } : { isDefault: false }),
      saveType,
      ...(defaultVersionVal ? { defaultVersionVal } : {})
    };

    try {
      const response = await axios({
        method: methodType,
        url: `${env.EVG_SVC_URL}${env.EVERGREEN_API_BASE_URL}${endpoint}`,
        data: payload,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });

      // const response = await axios.post(`${env.EVERGREEN_API_BASE_URL}${endpoint}`, payload, {
      //   headers: {
      //     'Content-Type': 'application/json',
      //     Authorization: `Bearer ${token}`
      //   }
      // });

      if (response.data.success) {
        Framework.getStoreRegistry()
          .getStore('notificationState')
          .set({
            open: true,
            type: 'success',
            message: response?.data.data ? response?.data.data : 'Graph saved successfully'
          });
        setCurrentGraphName(graphName);
        if (!isUpdate) {
          setCurrentGraphId(response?.data.graphId);
          setCurrentGraphVersion(response?.data.version);
          setCurrentGraphStatus(response?.data.lifeCycleStatus);
        }
        if (saveNPublishCheck) {
          setCurrentGraphStatus(response?.data.lifeCycleStatus);
        }
        const graphIdToSelect = !isUpdate ? response?.data.graphId : currentGraphId;
        handleGraphSelect(graphIdToSelect, false, response?.data.version || version);
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      Framework.getStoreRegistry()
        .getStore('notificationState')
        .set({
          open: true,
          type: 'error',
          message: `Failed to save graph: ${err.message}`
        });
    } finally {
      setSaveType('');
    }
  };

  // Load graph data from the backend
  const handleLoadGraph = () => {
    setShowGraphList(true);
  };

  const handleGraphSelect = async (graphId, flag, version = '') => {
    setShowPopup(flag);

    if (flag) {
      setFetchedGraphId(graphId);
      setSaveType('copy');
      return;
    }
    const token = utils.getToken();
    try {
      // filename = filename.replace(/\.json$/, '');
      const response = await axios.get(`${env.EVERGREEN_API_BASE_URL}/graphs/${graphId}?version=${version}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'x-berlin-tenant-id': selectedTenantId
        }
      });
      const data = response.data;

      if (!data.success) {
        const notification = {
          open: true,
          type: 'error',
          message: `Error loading graph: ${data.message}`
        };
        Framework.getStoreRegistry().getStore('notificationState').set(notification);
        // alert(`Error loading graph: ${data.message}`);
        return;
      }

      setCurrentLiveVersionList(data?.graph.versionList);

      setCurrentGraphVersion(data?.graph.version);

      setCurrentGraphStatus(data?.graph.lifeCycleStatus);

      setCurrentGraphName(data?.graph.name);

      setCurrentGraphId(data?.graph.graphId);

      setSelectedDefaultVersion(data?.graph.isDefault);

      setDefaultVersion(
        globalGraphs.filter((graph) => graph.graphId === data?.graph.graphId).find((graph) => graph.isDefault === true)
          ?.version
      );

      const { graph } = data;

      if (!graph.nodes || !Array.isArray(graph.nodes)) {
        console.error('Invalid graph data:', graph);
        const notification = {
          open: true,
          type: 'error',
          message: 'Invalid graph data received'
        };
        Framework.getStoreRegistry().getStore('notificationState').set(notification);
        // alert('Invalid graph data received');
        return;
      }

      // Transform loaded nodes to include all necessary properties
      const nodesWithPositions = graph.nodes.map((node, index) => {
        const nodeGrouping = {};
        if (node?.parentId) nodeGrouping.parentId = node?.parentId;
        if (node?.extent) nodeGrouping.extent = node?.extent;
        if (node?.positionAbsolute) nodeGrouping.positionAbsolute = node?.positionAbsolute;
        if (node?.width) nodeGrouping.width = node?.width;
        if (node?.height) nodeGrouping.height = node?.height;

        // Base node structure
        const baseNode = {
          id: node.id,
          type: node.customType || 'default', // Use customType as the actual type
          position: node.position || {
            x: 100 + index * 150,
            y: 100 + index * 50
          },
          draggable: true,
          selectable: true,
          ...nodeGrouping,
          style: {
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            // transition: 'box-shadow 0.3s, transform 0.1s',
            cursor: 'move'
          }
        };

        if (node?.style) baseNode.style = { ...baseNode.style, ...node.style };

        // Handle different node types
        if (node.customType === 'agent' || node.customType === 'supervisor') {
          baseNode.data = {
            prompt: node.data.label,
            description: node.data.description,
            intentType: node.data.intentType,
            config: {
              model: {
                provider: node.data.agentConfig?.provider || 'open-ai',
                model: node.data.agentConfig?.model || 'gpt-4o',
                temperature: node.data.agentConfig?.temperature ?? 0.7,
                maxTokens: node.data.agentConfig?.maxTokens || 1000,
                promptType: node.data.agentConfig?.promptType || ''
              },
              otherParameters: node.data.agentConfig?.otherParameters,
              additionalMetadata: node.data.agentConfig?.additionalMetadata,
              systemPrompt:
                node.data.agentConfig?.prompt ||
                'You are a super helpful teacher that explains student questions step by step. You should explain for grade 5 students. Always try to cheer the student up and make them feel good.',
              template: '{{input}}',
              memory: {
                type: 'buffer',
                maxMessages: 10,
                reflectionThreshold: 5
              },
              toolCalling: {
                enabled: true,
                toolChoice: 'auto'
              },
              functions: [],
              state: {
                variables: []
              },
              parsers: node.data.parsers || [],
              // Include agentConfig with GPT-5 specific fields if they exist
              ...(node.data.agentConfig?.reasoningEffort || node.data.agentConfig?.verbosity
                ? {
                    agentConfig: {
                      ...(node.data.agentConfig.reasoningEffort && {
                        reasoningEffort: node.data.agentConfig.reasoningEffort
                      }),
                      ...(node.data.agentConfig.verbosity && { verbosity: node.data.agentConfig.verbosity })
                    }
                  }
                : {})
            }
          };
        } else if (node.customType === 'tool') {
          baseNode.data = {
            tool_name: node.data.label,
            toolId: node.data.toolId, // Preserve the toolId from saved data
            description: node.data.description,
            config: {
              name: node.data.toolConfig?.jsMethod || node.data.label,
              description: 'This tool performs a addition of two numbers.',
              parameters: JSON.parse(node.data.toolConfig?.zodSchema || '{"type":"object","properties":{}}'),
              implementation: {
                type: 'function',
                code:
                  node.data.toolConfig?.jsMethodCode ||
                  `async ({ a, b }) => a + b,
  {
    name:        'add',
    description: 'Adds two numbers together',
    schema:      z.object({
      a: z.number().describe('first number'),
      b: z.number().describe('second number'),
    }),
  }`
              },
              responseFormat: node.data.toolConfig?.responseFormat,
              accessControl: {
                requiresAuth: false,
                rateLimit: 100
              }
            },
            toolConfig: node.data.toolConfig
          };
        } else {
          // For start and end nodes
          baseNode.data = {
            label: node.data.label,
            description: node.data.description
          };
        }

        return baseNode;
      });

      // Handle edges with proper styling
      const edgesWithData = graph.edges
        ? graph.edges.map((edge) => {
            // Find source and target nodes to get their types
            const sourceNode = nodesWithPositions.find((n) => n.id === edge.source);
            const targetNode = nodesWithPositions.find((n) => n.id === edge.target);
            const sourceType = sourceNode?.type || 'agent';
            const targetType = targetNode?.type || 'agent';

            // Log edge details for debugging
            console.log('Loading edge:', {
              id: edge.id,
              source: edge.source,
              target: edge.target,
              sourceType,
              targetType,
              conditional: edge.condition ? true : false
            });

            // Determine edge color based on source and target node types
            let edgeColor;
            if (sourceType === 'agent' && targetType === 'tool') {
              edgeColor = '#a78bfa'; // agent color
            } else if (sourceType === 'tool') {
              edgeColor = '#fcd34d'; // tool color
            } else if (sourceType === 'start') {
              edgeColor = '#4ade80'; // start color
            } else if (sourceType === 'end') {
              edgeColor = '#fb923c'; // end color
            } else if (sourceType === 'supervisor') {
              edgeColor = '#eb6134'; // supervisor color
            } else {
              edgeColor = '#a78bfa'; // default to agent color
            }

            return {
              id: edge.id,
              source: edge.source,
              target: edge.target,
              type: 'custom',
              animated: true,
              data: {
                label: edge.label || 'continue',
                sourceType,
                targetType,
                conditional: edge.condition ? true : false,
                conditionExpression: edge.condition?.expression || 'true',
                bidirectional: false // Will be updated by the edge processing effect
              },
              style: {
                stroke: edgeColor,
                strokeDasharray: edge.condition ? '5,5' : 'none'
              },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: edgeColor
              },
              sourceHandle: edge?.sourceHandle || '',
              targetHandle: edge?.targetHandle || ''
            };
          })
        : [];

      setNodesStable(nodesWithPositions);
      setEdgesStable(edgesWithData);
      // if(!showPopup){
      //   setShowGraphList(false);
      // }
      setShowGraphList(false);
    } catch (error) {
      console.error('Error loading graph:', error);
      const notification = {
        open: true,
        type: 'error',
        message: 'Failed to load graph. Please try again.'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      // alert('Failed to load graph. Please try again.');
    }
  };

  const onHandleVersionChange = (event) => {
    //selectedVersion.graphId, false, selectedVersion.version
    handleGraphSelect(currentGraphId, false, event.target.value);
  };

  const handleTenantChange = (event) => {
    const { tenantList } = tenantState;
    const selectedId = event.target.value;
    const tenant = tenantList.find((t) => t.tenantId === selectedId);

    setSelectedTenantId(selectedId);
    tenantState.setSelectedTenant(tenant?.name);
  };

  const handleCloseModal = () => {
    setShowPopup(false);
  };

  const handleSaveGraph = () => {
    if (currentGraphId && currentGraphStatus === constants.PROMPT_LIVE_STATUS) {
      setSaveType('saveChanges');
      setShowPopup(true);
      // this is for save and publish
      // handleCallSaveGraph(currentGraphName)
    } else if (currentGraphId) {
      // this is for draft graph which loaded and making change
      handleCallSaveGraph(currentGraphName);
    } else {
      // this is for new graph where user enter the name
      setSaveType('graphNameChanges');
      setShowPopup(true);
    }
  };

  const saveNewGraph = (popupValue, isDefault, defaultVersionVal) => {
    if (saveType === 'copyGraph') {
      createCopyGraph(popupValue);
    } else if (currentGraphStatus === constants.PROMPT_LIVE_STATUS || saveType === 'saveChanges') {
      setShowPopup(false);
      handleSaveOnPublish(popupValue, isDefault, defaultVersionVal);
    } else if (saveType === 'publishChanges') {
      handleCallSaveGraph(currentGraphName, popupValue, isDefault, defaultVersionVal);
    } else {
      handleCallSaveGraph(popupValue);
    }
  };

  const handleSaveOnPublish = async (comment, isDefault, defaultVersionVal) => {
    if (currentGraphStatus === constants.PROMPT_LIVE_STATUS) {
      handleCallSaveGraph(currentGraphName, comment, isDefault, defaultVersionVal);
    }
  };

  const handlePublishGraph = () => {
    if (!currentGraphName) return;
    // if (currentGraphStatus === constants.PROMPT_LIVE_STATUS) {
    //   setSaveType('saveChanges');
    // } else {
    //   setSaveType('publishChanges');
    // }
    setSaveType('publishChanges');
    setShowPopup(true);
  };

  useEffect(() => {
    if (saveType === 'copyGraph') {
      setShowPopup(true);
    }
  }, [saveType]);

  const createCopyGraph = async (graphName) => {
    const token = utils.getToken();
    // const uuid = utils.uuid();

    const transformedNodes = nodes.map(transformNode);
    const transformedEdges = edges.map((edge) => transformEdge(edge, nodes));

    const graphData = {
      tenantId: selectedTenantId,
      nodes: transformedNodes,
      edges: transformedEdges
    };

    const payload = {
      graphName,
      graphData
    };

    try {
      const response = await axios({
        method: 'POST',
        url: `${env.EVERGREEN_API_BASE_URL}/graphs`,
        data: payload,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'x-berlin-tenant-id': selectedTenantId
        }
      });

      if (response.data.success) {
        Framework.getStoreRegistry().getStore('notificationState').set({
          open: true,
          type: 'success',
          message: 'Graph saved successfully'
        });
        setCurrentGraphName(graphName);
        setCurrentGraphId(response?.data.graphId);
        setCurrentGraphVersion(response?.data.version);
        setCurrentGraphStatus(response?.data.lifeCycleStatus);
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      Framework.getStoreRegistry()
        .getStore('notificationState')
        .set({
          open: true,
          type: 'error',
          message: `Failed to save graph: ${err.message}`
        });
    } finally {
      setSaveType('');
    }
  };

  const handleCopyGraph = () => {
    if (!currentGraphName) return;
    setSaveType('copyGraph');
    return;
  };

  return (
    <div ref={containerRef}>
      {!isFullScreen && (
        <Header
          currentGraphName={currentGraphName || ''}
          onTenantChange={handleTenantChange}
          graphVersion={currentGraphVersion}
          graphStatus={currentGraphStatus}
          graphVersions={versionList}
          onVersionChange={onHandleVersionChange}
        />
      )}
      {/* <div>
        <h1 style={titleStyle}>Berlin Studio</h1>
      </div> */}
      {showPopup && (
        <ModalPopup
          close={handleCloseModal}
          select={saveNewGraph}
          config={saveType}
          selectedDefault={selectedDefaultVersion}
          defaultVersion={defaultVersion}
          currentLiveVersionList={currentLiveVersionList}
          currentGraphStatus={currentGraphStatus}
        />
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 725,
          height: 'calc(100vh - 4rem)'
        }}
      >
        {/* Show graph list popup when showGraphList is true */}
        {showGraphList && (
          <GraphListPopup
            onClose={() => setShowGraphList(false)}
            onSelect={handleGraphSelect}
            setVersionList={setVersionList}
            tenantId={selectedTenantId}
            handleReset={handleReset}
            currentGraphId={currentGraphId}
            canEditGraph={isAdminUser}
          />
        )}

        <div style={{ display: 'flex', flex: 1 }}>
          {/* Left Menu Sidebar */}
          <SidePanel
            handleNewGraph={handleNewGraph}
            handleReset={handleReset}
            handleLoadGraph={handleLoadGraph}
            handleSaveGraph={handleSaveGraph}
            handleAddNode={handleAddNode}
            handlePublishGraph={handlePublishGraph}
            handleCopyGraph={handleCopyGraph}
            handleConfigureNodeClick={handleConfigureNodeClick}
            sidebarStyles={sidebarStyles}
            buttonStyles={buttonStyles}
            isConditionalEdge={isConditionalEdge}
            setIsConditionalEdge={setIsConditionalEdge}
            canEditGraph={isAdminUser}
          />

          {/* Main Area for React Flow Canvas */}
          <div
            className={isDarkMode ? 'dark' : ''}
            style={{ flexGrow: 1, position: 'relative', color: isDarkMode ? 'white' : 'black' }}
            ref={reactFlowWrapper}
          >
            {/* <button onClick={() => setIsDarkMode((prev) => !prev)} style={THEME_BTN}>
              {isDarkMode ? <LightModeRoundedIcon /> : <DarkModeRoundedIcon />}
            </button> */}
            <ReactFlowProvider key="flow-provider">
              <Instructions />

              {currentGraphId && (
                <button onClick={() => setIsTesterOpen(true)} style={TEST_BTN}>
                  Test Graph
                </button>
              )}

              <Flow
                nodes={nodes}
                edges={edges}
                setNodes={setNodesStable}
                setEdges={setEdgesStable}
                isConditionalEdge={isConditionalEdge}
                onNodeDoubleClick={handleNodeDoubleClick}
                theme={isDarkMode}
                containerRef={containerRef}
                isFullScreen={isFullScreen}
                setIsFullScreen={setIsFullScreen}
              />
            </ReactFlowProvider>
          </div>
        </div>

        {/* Graph Tester Modal */}
        {currentGraphName && selectedTenantId && (
          <GraphTester
            graphName={currentGraphName}
            graphId={currentGraphId}
            graphVersion={currentGraphVersion}
            isOpen={isTesterOpen}
            onClose={() => setIsTesterOpen(false)}
            bookList={bookDataList}
            tenantId={selectedTenantId}
          />
        )}

        {/* Edit Node Modal */}
        <EditNodeModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          nodeType={nodeTypeToEdit}
          nodeData={selectedNode?.data}
          onSave={handleSaveNodeChanges}
          tenantId={selectedTenantId}
          providerList={providerList || []}
          promptTypesList={promptTypes}
          canEditGraph={isAdminUser}
        />
      </div>
    </div>
  );
};

GraphEditor.propTypes = {
  tenantState: shapes.modelOf(TenantsModel).isRequired
};

export default inject('userPermissions', 'userPermissionCheck', 'tenantState')(observer(GraphEditor));
